'use client';

/**
 * RecentlyViewedRail — horizontal scroll rail of recently-viewed
 * products, rendered at the top of the catalog page (M-7).
 *
 * Spec ref: SPEC-MARKETPLACE-QA-SMOKE-TEST-2026-10-03 item M-7.
 *
 * Behavior:
 *   - Reads `localStorage['profileia:recently-viewed']` on mount
 *     (SSR-safe — renders nothing during SSR, hydrates after mount).
 *   - Slices to the most recent 8 items (per the M-7 spec).
 *   - Only renders the section if there are ≥1 recently viewed items
 *     (per the M-7 spec — no empty-state UI).
 *   - Renders each item via the existing `<ProductCard />` component
 *     (per the M-7 spec — no parallel "recent card" implementation,
 *     CR-CHECKLIST §H2 — fix de raíz).
 *
 * Layout:
 *   - Section header "Visto recientemente" (h2).
 *   - Horizontal scroll rail (`overflow-x-auto` + `flex-nowrap`) of
 *     fixed-width cards (`min-w-[220px] max-w-[220px]`) — mirrors the
 *     pattern used by Netflix-style rails + the existing wishlist /
 *     combos horizontal rails in the marketplace.
 *   - Each card is a `<ProductCard />` constructed from the
 *     `RecentlyViewedItem` payload via `toPublicProductResponse`.
 *
 * Why a transformer instead of a separate "recent card" component?
 *   The M-7 spec mandates "using the existing `<ProductCard />`
 *   component". ProductCard expects a full `PublicProductResponse`.
 *   Constructing a `PublicProductResponse`-shaped object with safe
 *   defaults for the fields ProductCard doesn't read (description,
 *   variations, fulfillment, etc.) is the correct fix de raíz:
 *   - Single source of truth (PublicProductResponse) — no parallel
 *     type, no parallel card.
 *   - The transformer is centralized here (single file owns the
 *     shape lie); ProductCard's public API stays untouched.
 *
 * §6 Anti-Invención:
 *   - NO `as any` (P-041). The transformer uses an explicit object
 *     literal with all required `PublicProductResponse` fields —
 *     TypeScript verifies the shape.
 *   - NO `console.*` (P-042).
 *   - ARIA (P-044): the section has `aria-label` + the rail has
 *     `role="list"` + each card wrapper has `role="listitem"`.
 */

import { useEffect, useState } from 'react';
import { Clock } from 'lucide-react';
import { ProductCard } from '@/components/market/product-card';
import {
  loadRecentlyViewed,
  RECENTLY_VIEWED_RAIL_MAX_ITEMS,
  type RecentlyViewedItem,
} from '@/lib/recently-viewed';
import type { PublicProductResponse, ProductStatus } from '@profileia/shared';

/**
 * Transform a `RecentlyViewedItem` (localStorage payload) into a
 * `PublicProductResponse`-shaped object that `<ProductCard />` can
 * render. The fields ProductCard doesn't read are filled with safe
 * defaults (empty string, empty array, null, etc.).
 */
function toPublicProductResponse(item: RecentlyViewedItem): PublicProductResponse {
  return {
    productId: item.productId,
    type: item.type,
    singlePurchaseRecurring: item.singlePurchaseRecurring,
    category: item.category,
    subcategory: '',
    title: item.title,
    slug: item.slug,
    description: '',
    priceCredits: item.priceCredits,
    images: item.images,
    videoUrl: null,
    variations: [],
    stock: null,
    fulfillment: { inheritSeller: true, shippingOverride: null },
    serviceConfig: null,
    digitalConfig: null,
    status: 'approved' as ProductStatus,
    rating: item.rating,
    location: null,
    seller: {
      storeName: item.sellerStoreName,
      storeSlug: item.storeSlug,
      rating: 0,
      fulfillmentOptions: {
        ships: false,
        pickup: false,
        shipping: null,
        pickupConfig: null,
      },
    },
    storeSlug: item.storeSlug,
  };
}

export function RecentlyViewedRail() {
  // SSR-safe: render nothing during SSR; populate on mount. Avoids
  // hydration mismatch (localStorage is browser-only — the server
  // render + first client render must produce the same DOM).
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);

  useEffect(() => {
    setItems(loadRecentlyViewed().slice(0, RECENTLY_VIEWED_RAIL_MAX_ITEMS));
  }, []);

  // Listen for cross-tab updates (storage event) so the rail stays in
  // sync if the user opens a product in another tab. Cheap + correct.
  useEffect(() => {
    function onStorage(e: StorageEvent) {
      if (e.key === 'profileia:recently-viewed') {
        setItems(loadRecentlyViewed().slice(0, RECENTLY_VIEWED_RAIL_MAX_ITEMS));
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  if (items.length === 0) return null;

  return (
    <section
      className="mb-8"
      aria-label="Visto recientemente"
    >
      <h2 className="mb-3 flex items-center gap-2 text-lg font-semibold">
        <Clock className="size-5 text-muted-foreground" aria-hidden="true" />
        Visto recientemente
      </h2>
      <div
        role="list"
        className="flex gap-4 overflow-x-auto pb-2 snap-x snap-mandatory"
        style={{ scrollbarWidth: 'thin' }}
      >
        {items.map((item) => (
          <div
            key={item.productId}
            role="listitem"
            className="snap-start shrink-0 w-[220px] max-w-[220px]"
          >
            <ProductCard product={toPublicProductResponse(item)} />
          </div>
        ))}
      </div>
    </section>
  );
}
